/**
 * Spotlight overlay renderer.
 *
 * Implementation notes:
 *  - Single full-viewport SVG with an `<rect>` punched out via `mask` so we
 *    get one fixed-position scrim and one cutout regardless of how many
 *    coaching cards are open.
 *  - Pointer-events: none on the SVG surface itself; the underlying button
 *    remains clickable because nothing intercepts the click.
 *  - The coaching card slot is rendered as a sibling — the runner fills it.
 *  - Reduced-motion (`prefers-reduced-motion: reduce`):
 *      - no fade-in
 *      - no smooth-rect transition (the cutout snaps directly to the new
 *        position when the spotlight target changes)
 *
 * Phase 7 hardening:
 *  - smooth interpolation of the cutout rect across step transitions
 *  - shared `useReducedMotion` hook (Phase 7) replaces the local probe
 *  - explicit `role="presentation"` on the SVG so AT skips it
 */

import { useEffect, useState, type ReactNode } from "react";
import type { TargetRect } from "./useTargetRect";
import { useReducedMotion } from "../ui/useReducedMotion";

interface SpotlightRendererProps {
  rect: TargetRect | null;
  padding?: number;
  cornerRadius?: number;
  card?: ReactNode;
  onScrimClick?: () => void;
}

const DEFAULT_PADDING = 6;
const DEFAULT_RADIUS = 8;
const RECT_TRANSITION_MS = 260;

export function SpotlightRenderer({
  rect,
  padding = DEFAULT_PADDING,
  cornerRadius = DEFAULT_RADIUS,
  card,
  onScrimClick,
}: SpotlightRendererProps) {
  const reduced = useReducedMotion();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  if (!rect) {
    // No target → render a full scrim with no cutout (modal-style steps).
    return (
      <div
        aria-hidden="true"
        onClick={onScrimClick}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.55)",
          zIndex: 9998,
          opacity: mounted || reduced ? 1 : 0,
          transition: reduced ? "none" : "opacity 180ms ease-out",
          pointerEvents: onScrimClick ? "auto" : "none",
        }}
      >
        {card}
      </div>
    );
  }

  const x = rect.x - padding;
  const y = rect.y - padding;
  const w = rect.w + padding * 2;
  const h = rect.h + padding * 2;

  // The SVG attribute transition is what gives us the smooth movement
  // between two targets. We toggle it off entirely for reduced motion.
  const rectTransition = reduced
    ? undefined
    : `x ${RECT_TRANSITION_MS}ms ease-out, y ${RECT_TRANSITION_MS}ms ease-out, width ${RECT_TRANSITION_MS}ms ease-out, height ${RECT_TRANSITION_MS}ms ease-out`;

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        pointerEvents: "none",
        opacity: mounted || reduced ? 1 : 0,
        transition: reduced ? "none" : "opacity 180ms ease-out",
      }}
    >
      <svg
        role="presentation"
        width="100%"
        height="100%"
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: onScrimClick ? "auto" : "none",
        }}
        onClick={onScrimClick}
      >
        <defs>
          <mask id="onboarding-spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            <rect
              x={x}
              y={y}
              width={w}
              height={h}
              rx={cornerRadius}
              ry={cornerRadius}
              fill="black"
              style={{ transition: rectTransition }}
            />
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(15, 23, 42, 0.55)"
          mask="url(#onboarding-spotlight-mask)"
        />
        <rect
          x={x}
          y={y}
          width={w}
          height={h}
          rx={cornerRadius}
          ry={cornerRadius}
          fill="none"
          stroke="rgb(99, 102, 241)"
          strokeWidth={2}
          pointerEvents="none"
          style={{ transition: rectTransition }}
        />
      </svg>
      {card}
    </div>
  );
}
